import * as React from "react";
import { Route, Routes } from "react-router";
import { Public } from "./components/pages/public/Public";
import { ProvideConfiguration } from "./hooks/useConfiguration";
import { ProvideAuth } from "./hooks/useAuth";
import { ProvideGoogleTagManager } from "./hooks/useGoogleTagManager";
import { ToastContainer, Slide } from "react-toastify";
import AppRoutes from "./AppRoutes";
import { ErrorPage } from "./ErrorPage";
import { Loading } from "@commonComponents/ui/Loading";
import smoothscroll from "smoothscroll-polyfill";
import ExternalRoute from "@commonClient/src/ExternalRoute";
import { initReo } from "@commonClient/src/utils/reoService";

interface State {
    hasError?: boolean;
}

smoothscroll.polyfill();

const Portal = React.lazy(() => import("./components/pages/portal/Portal").then(({ Portal }) => ({ default: Portal })));

const AzureMarketplaceSubscriptionRegistrationPage = React.lazy(
    () =>
        import(
            /* webpackChunkName: "Azure Marketplace Subscription Registration" */ "./components/pages/public/azureMarketplace/AzureMarketplaceSubscriptionRegistrationPage"
        )
);

const AzureMarketplaceSubscriptionRegistrationThankYouPage = React.lazy(
    () =>
        import(
            /* webpackChunkName: "Azure Marketplace Subscription Registration Thank You" */ "./components/pages/public/azureMarketplace/AzureMarketplaceSubscriptionRegistrationThankYouPage"
        )
);

const AwsMarketplaceCustomerRegistrationPage = React.lazy(
    () =>
        import(
            /* webpackChunkName: "Aws Marketplace Customer Registration" */ "./components/pages/public/awsMarketplace/AwsMarketplaceCustomerRegistrationPage"
        )
);

const AwsMarketplaceCustomerRegistrationThankYouPage = React.lazy(
    () =>
        import(
            /* webpackChunkName: "Aws Marketplace Customer Registration Thank You" */ "./components/pages/public/awsMarketplace/AwsMarketplaceCustomerRegistrationThankYouPage"
        )
);

export default class App extends React.Component<Record<string, never>, State> {
    constructor() {
        super(undefined);

        this.state = {
            hasError: false,
        };
    }

    static getDerivedStateFromError() {
        return {
            hasError: true,
        };
    }

    async componentDidMount() {
        await initReo();
    }

    componentDidCatch(): void {
        //TODO: do we want to report that to GA?
    }

    public render() {
        return (
            <ProvideAuth>
                <ProvideConfiguration>
                    <ProvideGoogleTagManager>
                        <React.Suspense fallback={<Loading />}>
                            <AppRoutesComponent hasError={this.state.hasError} />
                        </React.Suspense>
                        <ToastContainer transition={Slide} />
                    </ProvideGoogleTagManager>
                </ProvideConfiguration>
            </ProvideAuth>
        );
    }
}

interface AppRoutesComponentProps {
    hasError: boolean;
}

function AppRoutesComponent({ hasError }: AppRoutesComponentProps) {
    if (hasError) {
        return <ErrorPage />;
    }

    return (
        <Routes>
            <Route
                path={AppRoutes.forStatus().linkForRoute}
                element={<ExternalRoute url="https://status.ravendb.net/" />}
            />
            <Route
                path={AppRoutes.forAzureMarketplaceSubscriptionRegistration().linkForRoute}
                element={<AzureMarketplaceSubscriptionRegistrationPage />}
            />
            <Route
                path={AppRoutes.forAzureMarketplaceSubscriptionRegistrationThankYou().linkForRoute}
                element={<AzureMarketplaceSubscriptionRegistrationThankYouPage />}
            />
            <Route
                path={AppRoutes.forAwsMarketplaceSubscriptionRegistration().linkForRoute}
                element={<AwsMarketplaceCustomerRegistrationPage />}
            />
            <Route
                path={AppRoutes.forAwsMarketplaceSubscriptionRegistrationThankYou().linkForRoute}
                element={<AwsMarketplaceCustomerRegistrationThankYouPage />}
            />
            <Route path={AppRoutes.forPortalSection().linkForRoute} element={<Portal />} />
            <Route path={AppRoutes.forPublicSection().linkForRoute} element={<Public />} />
        </Routes>
    );
}
